/* ==========================================================================
   TapedUp theme interactions.
   Vanilla JS, no framework, no jQuery. Cart/search use Shopify's native
   AJAX endpoints (/cart/add.js, /cart/change.js, /cart.js,
   /search/suggest.json) plus the Section Rendering API so drawer markup is
   always generated by Liquid, never duplicated in JS.
   ========================================================================== */
(() => {
  'use strict';

  const money = (cents) => {
    const format = (window.themeStrings && window.themeStrings.moneyFormat) || '£{{amount}}';
    return format.replace('{{amount}}', (cents / 100).toFixed(2));
  };

  /* ---------------------------------------------------------------------
   * Drawers (cart / search / menu) -- one shared shell + focus trap
   * ------------------------------------------------------------------- */
  class TuDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.tu-drawer__overlay');
      this.panel = this.querySelector('.tu-drawer__panel');
      this.closers = this.querySelectorAll('[data-drawer-close]');
      this._onKeydown = this._onKeydown.bind(this);
      this.overlay && this.overlay.addEventListener('click', () => this.close());
      this.closers.forEach((el) => el.addEventListener('click', () => this.close()));
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);
      const focusable = this.panel.querySelector('input, button, a, [tabindex]');
      window.requestAnimationFrame(() => focusable && focusable.focus());
      this.dispatchEvent(new CustomEvent('tu:drawer:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      if (this.trigger && this.trigger.focus) this.trigger.focus();
      this.dispatchEvent(new CustomEvent('tu:drawer:close', { bubbles: true }));
    }

    _onKeydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusables = this.panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }
  customElements.define('tu-drawer', TuDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const id = opener.getAttribute('data-drawer-open');
    const drawer = document.getElementById(`tu-drawer-${id}`);
    if (drawer) { event.preventDefault(); drawer.open(opener); }
  });

  /* ---------------------------------------------------------------------
   * Toast
   * ------------------------------------------------------------------- */
  function tuToast(text) {
    const el = document.getElementById('tu-toast');
    if (!el) return;
    el.querySelector('[data-toast-text]').textContent = text;
    el.classList.add('is-visible');
    clearTimeout(tuToast._t);
    tuToast._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }
  window.tuToast = tuToast;

  /* ---------------------------------------------------------------------
   * Cart: shared add / refresh helpers
   * ------------------------------------------------------------------- */
  const CartAPI = {
    async add(items) {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      return res.json();
    },
    async change(line, quantity) {
      const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      return res.json();
    },
    async get() {
      const res = await fetch(window.Shopify.routes.root + 'cart.js');
      return res.json();
    },
    async refreshDrawer() {
      const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = html.getElementById('tu-drawer-cart');
      const current = document.getElementById('tu-drawer-cart');
      if (next && current) {
        const wasOpen = current.classList.contains('is-open');
        current.replaceWith(next);
        if (wasOpen) next.classList.add('is-open');
        updateCartCount();
      }
      CartAPI.refreshBundleBar();
      CartAPI.refreshStarterBanner();
    },
    async refreshStarterBanner() {
      const banner = document.getElementById('tu-starter-banner');
      if (!banner) return;
      const res = await fetch(`${window.Shopify.routes.root}?section_id=starter-offer-banner`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = html.getElementById('tu-starter-banner');
      if (next) banner.replaceWith(next);
    },
    async refreshBundleBar() {
      const bar = document.getElementById('tu-bundle-bar');
      if (!bar) return; // section disabled or not on this page
      const res = await fetch(`${window.Shopify.routes.root}?section_id=bundle-progress-bar`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = html.getElementById('tu-bundle-bar');
      if (!next) return;
      // Keep the modal open (if it was) and the fab dismissed (if it was)
      // across this replace, same idea as the cart drawer's wasOpen above.
      const modal = document.getElementById('tu-drawer-bundle');
      const wasOpen = modal && modal.classList.contains('is-open');
      let dismissed = false;
      try { dismissed = sessionStorage.getItem('tuBundleBarDismissed') === '1'; } catch (err) { /* ignore */ }
      bar.replaceWith(next);
      if (wasOpen) {
        const nextModal = document.getElementById('tu-drawer-bundle');
        nextModal && nextModal.classList.add('is-open');
      }
      if (dismissed) {
        const nextFab = next.querySelector('.tu-bundle-fab');
        nextFab && nextFab.classList.add('tu-bundle-fab--hidden');
      }
    },
  };
  window.TuCartAPI = CartAPI;

  async function updateCartCount() {
    const cart = await CartAPI.get();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const changed = el.textContent !== String(cart.item_count);
      el.textContent = cart.item_count;
      const wrap = el.closest('[data-cart-badge-wrap]');
      if (wrap) {
        wrap.classList.toggle('is-empty', cart.item_count === 0);
        if (changed && cart.item_count > 0) {
          const badge = wrap.querySelector('.tu-cart-badge');
          if (badge) {
            badge.classList.remove('tu-cart-badge--bump');
            // eslint-disable-next-line no-unused-expressions
            badge.offsetWidth; // restart the animation on rapid repeat adds
            badge.classList.add('tu-cart-badge--bump');
          }
        }
      }
    });
    return cart;
  }
  updateCartCount();

  // Bundle progress fab: restore a dismissal for the rest of this browser
  // session (sessionStorage, wrapped since private-mode browsers can throw),
  // and let its close button set it. Dismissing only hides the floating
  // trigger -- the offer is still on, they just don't want the reminder.
  (() => {
    try {
      if (sessionStorage.getItem('tuBundleBarDismissed') === '1') {
        const fab = document.querySelector('.tu-bundle-fab');
        fab && fab.classList.add('tu-bundle-fab--hidden');
      }
    } catch (err) { /* private mode etc -- fab just stays visible */ }
  })();
  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-bundle-bar-close]');
    if (!close) return;
    const fab = close.closest('.tu-bundle-fab');
    if (!fab) return;
    fab.classList.add('tu-bundle-fab--hidden');
    try { sessionStorage.setItem('tuBundleBarDismissed', '1'); } catch (err) { /* ignore */ }
  });

  // Delegate: any [data-add-to-cart] button/form adds via AJAX
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-cart-add-form]');
    if (!form) return;
    event.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    submitBtn && submitBtn.setAttribute('disabled', 'disabled');
    try {
      const fd = new FormData(form);
      const id = fd.get('id');
      const quantity = Number(fd.get('quantity') || 1);
      await CartAPI.add([{ id, quantity }]);
      await CartAPI.refreshDrawer();
      tuToast(form.getAttribute('data-added-message') || 'Added to cart');
      const drawer = document.getElementById('tu-drawer-cart');
      drawer && drawer.open(submitBtn);
    } catch (err) {
      tuToast(err.message || 'Could not add to cart');
    } finally {
      submitBtn && submitBtn.removeAttribute('disabled');
    }
  });

  // Shared "add this variant, show the toast, refresh the drawer" flow used
  // by every quick-add path below (plain button / stepper confirm / variant
  // popover pill) so the try/catch/disable dance only lives in one place.
  async function quickAddCommit(triggerEl, cluster, id, quantity) {
    triggerEl.setAttribute('disabled', 'disabled');
    try {
      await CartAPI.add([{ id, quantity }]);
      await CartAPI.refreshDrawer();
      tuToast(`Added — ${cluster.getAttribute('data-product-title') || 'tape'}`);
      // Silent adds (e.g. the bundle modal's "add another design" grid) stay
      // put in their own panel instead of popping the cart drawer over it.
      if (!cluster.hasAttribute('data-quick-add-silent')) {
        const drawer = document.getElementById('tu-drawer-cart');
        drawer && drawer.open(triggerEl);
      }
    } catch (err) {
      tuToast(err.message || 'Could not add to cart');
    } finally {
      triggerEl.removeAttribute('disabled');
    }
  }

  function closeVariantPop(cluster) {
    const pop = cluster.querySelector('[data-variant-pop]');
    const confirmBtn = cluster.querySelector('[data-quick-add-confirm]');
    if (pop) pop.hidden = true;
    if (confirmBtn) confirmBtn.setAttribute('aria-expanded', 'false');
  }

  // [data-quick-add] -- either a stepper cluster (product-card quick add, with
  // [data-quick-add-qty]/-increase/-decrease/-confirm children) or a plain
  // single button (e.g. the cart-drawer upsell tiles), which just adds qty 1.
  // A stepper cluster with more than one variant (data-has-variants) opens a
  // popover of variant pills on confirm instead of adding immediately.
  document.addEventListener('click', async (event) => {
    const cluster = event.target.closest('[data-quick-add]');

    // Click landed on a variant pill inside an open popover -- add that
    // specific variant at the current stepper quantity.
    const choice = event.target.closest('[data-variant-choice]');
    if (choice && cluster) {
      event.preventDefault();
      if (choice.hasAttribute('disabled')) return;
      const qtyEl = cluster.querySelector('[data-quick-add-qty]');
      const quantity = Math.max(1, Number((qtyEl && qtyEl.textContent) || 1));
      await quickAddCommit(choice, cluster, choice.getAttribute('data-variant-id'), quantity);
      closeVariantPop(cluster);
      if (qtyEl) qtyEl.textContent = '1';
      return;
    }

    // Click outside any quick-add cluster -- close whatever popover is open.
    if (!cluster) {
      document.querySelectorAll('[data-variant-pop]:not([hidden])').forEach((pop) => {
        closeVariantPop(pop.closest('[data-quick-add]'));
      });
      return;
    }

    const qtyEl = cluster.querySelector('[data-quick-add-qty]');

    if (!qtyEl) {
      event.preventDefault();
      const plainId = cluster.getAttribute('data-variant-id');
      if (!plainId) return;
      await quickAddCommit(cluster, cluster, plainId, 1);
      return;
    }

    if (event.target.closest('[data-quick-add-increase]')) {
      qtyEl.textContent = Number(qtyEl.textContent || 1) + 1;
      return;
    }
    if (event.target.closest('[data-quick-add-decrease]')) {
      qtyEl.textContent = Math.max(1, Number(qtyEl.textContent || 1) - 1);
      return;
    }
    const confirmBtn = event.target.closest('[data-quick-add-confirm]');
    if (!confirmBtn) return;
    event.preventDefault();

    // Multi-variant product -- open the picker instead of adding straight away.
    if (cluster.hasAttribute('data-has-variants')) {
      const pop = cluster.querySelector('[data-variant-pop]');
      if (!pop) return;
      const willOpen = pop.hidden;
      // Close any other open popover first (only one at a time on the page).
      document.querySelectorAll('[data-variant-pop]:not([hidden])').forEach((other) => {
        if (other !== pop) closeVariantPop(other.closest('[data-quick-add]'));
      });
      pop.hidden = !willOpen;
      confirmBtn.setAttribute('aria-expanded', String(willOpen));
      return;
    }

    const id = cluster.getAttribute('data-variant-id');
    if (!id) return;
    const quantity = Math.max(1, Number(qtyEl.textContent || 1));
    await quickAddCommit(confirmBtn, cluster, id, quantity);
    qtyEl.textContent = '1'; // reset the stepper for the next add
  });

  // Escape closes any open variant popover.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-variant-pop]:not([hidden])').forEach((pop) => {
      closeVariantPop(pop.closest('[data-quick-add]'));
    });
  });

  // Delegate: cart line quantity +/- and remove
  document.addEventListener('click', async (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const wrap = step.closest('[data-qty]');
      const input = wrap.querySelector('[data-qty-input]');
      const dir = step.getAttribute('data-qty-step') === 'up' ? 1 : -1;
      const next = Math.max(0, Number(input.value || 1) + dir);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.getAttribute('data-cart-remove');
      await CartAPI.change(Number(line), 0);
      await CartAPI.refreshDrawer();
    }
  });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-qty-input][data-cart-line]');
    if (!input) return;
    const line = Number(input.getAttribute('data-cart-line'));
    const quantity = Math.max(0, Number(input.value || 0));
    await CartAPI.change(line, quantity);
    await CartAPI.refreshDrawer();
  });

  /* ---------------------------------------------------------------------
   * Variant picker (single "Width" option) -- swaps price/hint/variant id,
   * no page reload.
   * ------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-variant-pill]');
    if (!pill) return;
    const wrap = pill.closest('[data-variant-picker]');
    if (!wrap) return;
    wrap.querySelectorAll('[data-variant-pill]').forEach((el) => el.setAttribute('aria-pressed', 'false'));
    pill.setAttribute('aria-pressed', 'true');

    const variantId = pill.getAttribute('data-variant-id');
    const price = pill.getAttribute('data-variant-price');
    const compareAt = pill.getAttribute('data-variant-compare-at');
    const hint = pill.getAttribute('data-variant-hint');
    const available = pill.getAttribute('data-variant-available') !== 'false';

    const scope = wrap.closest('[data-product-root]') || document;
    const idInput = scope.querySelector('[data-selected-variant-id]');
    if (idInput) idInput.value = variantId;

    const priceEl = scope.querySelector('[data-variant-price-display]');
    if (priceEl) priceEl.textContent = price;
    const compareEl = scope.querySelector('[data-variant-compare-display]');
    if (compareEl) { compareEl.textContent = compareAt || ''; compareEl.hidden = !compareAt; }
    const hintEl = scope.querySelector('[data-variant-hint-display]');
    if (hintEl && hint) hintEl.textContent = hint;

    const submitBtn = scope.querySelector('[data-add-to-cart-btn]');
    if (submitBtn) submitBtn.disabled = !available;
    const submitLabel = scope.querySelector('[data-add-to-cart-label]');
    if (submitLabel) submitLabel.textContent = available ? submitLabel.getAttribute('data-label-available') : submitLabel.getAttribute('data-label-unavailable');

    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }
  });

  /* ---------------------------------------------------------------------
   * Quantity stepper generic (non-cart contexts, e.g. PDP)
   * ------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-plain]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    const min = Number(input.min || 1);
    const dir = btn.getAttribute('data-qty-plain') === 'up' ? 1 : -1;
    input.value = Math.max(min, Number(input.value || 1) + dir);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------------
   * Sticky add-to-cart bar -- shows once the PDP's own buy box scrolls
   * out of view.
   * ------------------------------------------------------------------- */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const buyBox = document.querySelector('[data-buy-box]');
  if (stickyBar && buyBox && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(buyBox);
  }

  /* ---------------------------------------------------------------------
   * Bundle builder (homepage "buy 4 get 1 free" picker)
   * ------------------------------------------------------------------- */
  const bundle = document.querySelector('[data-bundle-builder]');
  if (bundle) {
    const size = Number(bundle.getAttribute('data-bundle-size') || 5);
    const chips = Array.from(bundle.querySelectorAll('[data-bundle-chip]'));
    const bar = bundle.querySelector('[data-bundle-bar]');
    const status = bundle.querySelector('[data-bundle-status]');
    const cta = bundle.querySelector('[data-bundle-cta]');
    let picked = [];

    const templates = {
      status: bundle.getAttribute('data-status-template') || '{n} of {size} selected',
      cta: bundle.getAttribute('data-cta-template') || 'Add {size} to cart',
      ctaReady: bundle.getAttribute('data-cta-ready-template') || 'Add my {size} — pay for {paid}',
    };

    function render() {
      const n = picked.length;
      const pct = Math.min(100, (n / size) * 100);
      if (bar) bar.style.width = pct + '%';
      if (status) status.textContent = templates.status.replace('{n}', n).replace('{size}', size);
      if (cta) {
        cta.disabled = n < size;
        cta.textContent = n >= size
          ? templates.ctaReady.replace('{size}', size).replace('{paid}', size - 1)
          : templates.cta.replace('{size}', size).replace('{remaining}', size - n);
      }
      chips.forEach((chip) => chip.setAttribute('aria-pressed', picked.includes(chip.getAttribute('data-variant-id')) ? 'true' : 'false'));
    }

    bundle.addEventListener('click', async (event) => {
      const chip = event.target.closest('[data-bundle-chip]');
      if (chip) {
        const id = chip.getAttribute('data-variant-id');
        const idx = picked.indexOf(id);
        if (idx > -1) picked.splice(idx, 1);
        else if (picked.length < size) picked.push(id);
        render();
        return;
      }
      if (event.target.closest('[data-bundle-cta]') && picked.length >= size) {
        const btn = event.target.closest('[data-bundle-cta]');
        btn.disabled = true;
        try {
          const counts = picked.reduce((acc, id) => { acc[id] = (acc[id] || 0) + 1; return acc; }, {});
          const items = Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity }));
          await CartAPI.add(items);
          await CartAPI.refreshDrawer();
          tuToast('Added your five — the cheapest is free at checkout');
          picked = [];
          render();
          const drawer = document.getElementById('tu-drawer-cart');
          drawer && drawer.open(btn);
        } catch (err) {
          tuToast(err.message || 'Could not add bundle');
        } finally {
          btn.disabled = picked.length < size;
        }
      }
    });

    render();
  }

  /* ---------------------------------------------------------------------
   * Predictive search
   * ------------------------------------------------------------------- */
  const searchInput = document.querySelector('[data-predictive-search-input]');
  if (searchInput) {
    let controller;
    const results = document.querySelector('[data-predictive-search-results]');
    searchInput.addEventListener('input', async () => {
      const q = searchInput.value.trim();
      if (controller) controller.abort();
      if (!q) { results && (results.innerHTML = ''); return; }
      controller = new AbortController();
      try {
        const res = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`, { signal: controller.signal });
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (results) results.innerHTML = products.length
          ? products.map((p) => `<a class="tu-card" href="${p.url}">
              <span class="tu-card__media" style="aspect-ratio:1/1;overflow:hidden">${p.featured_image ? `<img src="${p.featured_image}" alt="" loading="lazy">` : ''}</span>
              <span class="tu-card__body">
                <span class="tu-card__title">${p.title}</span>
                <span class="tu-price"><span class="tu-price__amount">${p.price}</span></span>
              </span>
            </a>`).join('')
          : `<p style="color:var(--text-faint);font-size:13px">${window.themeStrings.noResults || 'No results found.'}</p>`;
      } catch (e) { /* aborted, ignore */ }
    });
  }

  /* ---------------------------------------------------------------------
   * Free-tape one-tap add (cart "one away" interrupt)
   * ------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-free-tape-add]');
    if (!btn) return;
    event.preventDefault();
    const id = btn.getAttribute('data-variant-id');
    btn.disabled = true;
    try {
      await CartAPI.add([{ id, quantity: 1 }]);
      await CartAPI.refreshDrawer();
      tuToast('Free tape added');
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------------------------------------------------------------------
   * B2B bulk pricing table (PDP) — live running total across every row,
   * recalculated on any quantity change (typing, or the +/- stepper, which
   * already dispatches a bubbling `change` on the input -- see the generic
   * [data-qty-plain] handler above). Cents math throughout to avoid float
   * drift when summing many rows, formatted with the same money() helper
   * cart totals use elsewhere.
   * ------------------------------------------------------------------- */
  function updateB2bTotal(table) {
    const totalEl = table.querySelector('[data-b2b-total]');
    if (!totalEl) return;
    const totalCents = Array.from(table.querySelectorAll('[data-b2b-qty]')).reduce((sum, input) => {
      const qty = Number(input.value || 0);
      const unitCents = Math.round(Number(input.getAttribute('data-unit-price') || 0) * 100);
      return sum + qty * unitCents;
    }, 0);
    totalEl.textContent = money(totalCents);
  }
  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-b2b-qty]');
    if (!input) return;
    const table = input.closest('[data-b2b-table]');
    table && updateB2bTotal(table);
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-b2b-qty]');
    if (!input) return;
    const table = input.closest('[data-b2b-table]');
    table && updateB2bTotal(table);
  });
  document.querySelectorAll('[data-b2b-table]').forEach(updateB2bTotal);

  /* ---------------------------------------------------------------------
   * B2B bulk pricing table (PDP) — "Add selected" gathers every
   * [data-b2b-qty] input with a quantity > 0 and adds them all in a
   * single /cart/add.js call, same pattern as the bundle builder.
   * ------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-b2b-add-all]');
    if (!btn) return;
    const table = btn.closest('[data-b2b-table]');
    if (!table) return;
    const items = Array.from(table.querySelectorAll('[data-b2b-qty]'))
      .map((input) => ({ id: Number(input.getAttribute('data-variant-id')), quantity: Number(input.value || 0) }))
      .filter((item) => item.quantity > 0);
    if (!items.length) {
      tuToast('Add a quantity first');
      return;
    }
    btn.disabled = true;
    try {
      await CartAPI.add(items);
      await CartAPI.refreshDrawer();
      tuToast('Added to cart');
      table.querySelectorAll('[data-b2b-qty]').forEach((input) => { input.value = 0; });
      updateB2bTotal(table);
      const drawer = document.getElementById('tu-drawer-cart');
      drawer && drawer.open(btn);
    } catch (err) {
      tuToast(err.message || 'Could not add to cart');
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------------------------------------------------------------------
   * Starter order (snippets/starter-offer.liquid). Adding it only tops the
   * cart up to the offer's quantities, in one /cart/add.js call -- so a
   * second click, or products already in the cart, never double it.
   * It's also added automatically once per offer per browser
   * (localStorage, keyed by when the offer was given, so a new or edited
   * offer auto-adds once more). After that, taking it out of the cart
   * sticks, and the banner / cart panel offer it back instead.
   * ------------------------------------------------------------------- */
  const starterOffer = (() => {
    const el = document.querySelector('script[data-starter-offer]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  })();

  async function addStarterOrder() {
    if (!starterOffer || !Array.isArray(starterOffer.items)) return false;
    const cart = await CartAPI.get();
    const inCart = new Map();
    cart.items.forEach((line) => inCart.set(line.variant_id, (inCart.get(line.variant_id) || 0) + line.quantity));
    const missing = starterOffer.items
      .map((item) => ({ id: item.id, quantity: item.quantity - (inCart.get(item.id) || 0) }))
      .filter((item) => item.quantity > 0);
    if (!missing.length) return false;
    await CartAPI.add(missing);
    return true;
  }

  async function showStarterOrderAdded(message, trigger) {
    // The standalone /cart page isn't re-rendered by the drawer refresh.
    if (starterOffer && starterOffer.cartPage) { window.location.reload(); return; }
    await CartAPI.refreshDrawer();
    tuToast(message);
    const drawer = document.getElementById('tu-drawer-cart');
    drawer && drawer.open(trigger);
  }

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-starter-add]');
    if (!btn) return;
    btn.disabled = true;
    try {
      await addStarterOrder();
      await showStarterOrderAdded('Starter order added', btn);
    } catch (err) {
      tuToast(err.message || 'Could not add to cart');
    } finally {
      btn.disabled = false;
    }
  });

  if (starterOffer && starterOffer.autoAdd !== false && starterOffer.key) {
    const flag = `tuStarterAutoAdded:${starterOffer.key}`;
    // Assume done unless storage can actually remember it -- without that
    // (private mode etc) an auto-add would come back on every page load.
    let alreadyAdded = true;
    try {
      alreadyAdded = localStorage.getItem(flag) === '1';
      if (!alreadyAdded) localStorage.setItem(flag, '1');
    } catch (err) { alreadyAdded = true; }
    if (!alreadyAdded) {
      addStarterOrder()
        .then((added) => added && showStarterOrderAdded('Your starter order is in your cart'))
        .catch(() => { /* e.g. out of stock -- the banner still offers it */ });
    }
  }

  /* ---------------------------------------------------------------------
   * Dealer PO / order-reference field — saved as the cart note, so it
   * shows up on the order for staff without needing any custom backend.
   * ------------------------------------------------------------------- */
  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-po-input]');
    if (!input) return;
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: input.value }),
      });
    } catch (err) { /* non-critical — checkout still works without the note saved */ }
  });

  /* ---------------------------------------------------------------------
   * Scroll-reveal — only runs at all when theme.liquid added
   * html.tu-reveal-ready (i.e. Theme settings > Motion > animations are on).
   * Marks every [data-reveal] element .is-revealed the first time it
   * crosses into the viewport, then stops observing it (one-shot).
   * ------------------------------------------------------------------- */
  if (document.documentElement.classList.contains('tu-reveal-ready') && 'IntersectionObserver' in window) {
    const revealSelector = [
      '.tu-heading', '.tu-hand', '.tu-card', '.tu-feature', '.tu-review-card',
      '.tu-post-card', '.tu-pros-cons__col', '.tu-sticker-cta', '.tu-newsletter',
      '.tu-photo-panel', '.tu-spec-list', '.tu-section-head',
    ].join(', ');

    const markGroups = () => {
      // Any element whose *direct children* are all reveal targets gets
      // data-reveal-group so the CSS nth-child stagger applies to it.
      document.querySelectorAll(revealSelector).forEach((el) => {
        const parent = el.parentElement;
        if (parent && !parent.hasAttribute('data-reveal-group') && parent.children.length > 1) {
          const siblings = Array.from(parent.children);
          if (siblings.every((sib) => sib.matches(revealSelector))) parent.setAttribute('data-reveal-group', '');
        }
      });
    };

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    const observeAll = (root) => {
      // Elements matched by class (the broad, automatic net) plus any
      // element a section author hand-marked with a bare data-reveal
      // attribute in Liquid (an explicit, deliberate opt-in).
      const nodes = root.querySelectorAll(`${revealSelector}, [data-reveal]`);
      nodes.forEach((el) => {
        if (el.getAttribute('data-reveal') === 'observed') return;
        el.setAttribute('data-reveal', 'observed');
        revealObserver.observe(el);
      });
    };

    markGroups();
    observeAll(document);

    // Cart drawer / any section re-rendered via the Section Rendering API
    // gets its own new elements wired up too.
    document.addEventListener('tu:drawer:open', (event) => {
      markGroups();
      observeAll(event.target);
    });

    // Theme editor: every time a merchant changes a setting, Shopify
    // replaces that section's entire HTML fresh via AJAX and fires
    // shopify:section:load on it. Without re-observing, the new elements
    // would sit at opacity:0 forever (they'd never intersect, since
    // observe() was never called on them) — this is what "changing one
    // setting makes everything disappear" actually was.
    document.addEventListener('shopify:section:load', (event) => {
      markGroups();
      observeAll(event.target);
    });
  }

  /* ---------------------------------------------------------------------
   * Hanging mascot — pinned to the viewport, top edge tracking the
   * header's actual current bottom edge (the header's height varies:
   * announcement bar on/off, mobile vs desktop layout, logo size — so this
   * is measured live rather than assumed). Motion:
   *   - a one-off entrance kick when the page loads (CSS keyframe,
   *     triggered by adding a class, removed once it finishes so it
   *     never replays);
   *   - a continuous subtle idle sway while the page is static (pure CSS,
   *     always running — see .tu-mascot__idle);
   *   - an extra reactive kick from actual scroll velocity, eased back to
   *     0 by the transition's bounce easing.
   * The idle loop and the JS-driven kicks live on two different nested
   * elements (see theme.css) so they compose instead of one `transform`
   * overwriting the other.
   * ------------------------------------------------------------------- */
  const mascotRoot = document.querySelector('[data-mascot]');
  const mascotSwing = document.querySelector('[data-mascot-swing]');
  const headerEl = document.querySelector('.tu-header');
  if (mascotRoot && headerEl) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = document.documentElement;

    const updateHeaderOffset = () => {
      root.style.setProperty('--tu-header-bottom', `${Math.round(headerEl.getBoundingClientRect().bottom)}px`);
    };
    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    if (mascotSwing && !reduceMotion) {
      mascotSwing.classList.add('tu-mascot--entrance');
      mascotSwing.addEventListener('animationend', () => {
        mascotSwing.classList.remove('tu-mascot--entrance');
      }, { once: true });
    }

    let lastY = window.scrollY;
    let ticking = false;
    let settleTimer = null;
    const maxAngle = 14;

    const onScroll = () => {
      updateHeaderOffset();
      if (mascotSwing && !reduceMotion) {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        const angle = Math.max(-maxAngle, Math.min(maxAngle, delta * 1.4));
        mascotSwing.style.transform = `rotate(${angle}deg)`;
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => { mascotSwing.style.transform = 'rotate(0deg)'; }, 140);
      }
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(onScroll);
    }, { passive: true });
  }
})();
